import { useQuery } from "react-query";
import { getProcessResult } from "api/process";
import { GET_PROCESS_RESULT } from "constants/reactQueryKeys";
import { useEffect } from "react";

/**
 * Custom hook for fetching the result of a process based on the invocation ID and target process.
 * This hook triggers the query to fetch the process result when the `invocationId` is provided and `enabled` is true.
 *
 * @category Hooks
 * @param {Object} options - Configuration options for the query.
 * @param {boolean} options.enabled - Determines if the query should be executed. If false, the query won't run.
 * @param {string} options.invocationId - The ID associated with the specific invocation to fetch results for.
 * @param {string} options.targetProcess - The type of process being fetched (e.g., "bulk_download").
 * @param {Function} [options.onSuccess] - Optional callback triggered when the request succeeds and data is fetched.
 * @param {Function} [options.onError] - Optional callback triggered if an error occurs during the request.
 * @returns {Object} The query result containing:
 *  - `resultError`: The error object if any error occurred during the query.
 *  - `data`: The fetched process result data or undefined if no data was retrieved.
 */
const useGetProcessResult = ({ enabled, invocationId, onSuccess, targetProcess, onError }) => {
  const { data, error } = useQuery(
    [GET_PROCESS_RESULT, invocationId],
    () => getProcessResult(invocationId, targetProcess),
    {
      enabled: !!invocationId && enabled,
      onSuccess,
    }
  );

  useEffect(() => {
    if (error) {
      onError(error);
    }
  }, [error, onError]);

  return { resultError: error, data };
};

export default useGetProcessResult;
